import { LEGAL_FIELD_LABELS, type LegalSettings } from "@cva/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Scale } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardHeader, Skeleton } from "../../components/ui/card";
import { Field, Input, Textarea } from "../../components/ui/input";
import { client, orpc } from "../../lib/api";
import { errorMessage, formatDateTime } from "../../lib/utils";
import { AdminPageHeader } from "./layout";

const PLACEHOLDERS: Partial<Record<keyof LegalSettings, string>> = {
  operatorName: "Max Mustermann bzw. Muster GmbH",
  legalForm: "z. B. Einzelunternehmen, GbR, GmbH",
  representative: "Geschäftsführer:in (nur bei Gesellschaften)",
  country: "Deutschland",
  vatId: "DE123456789",
  registerCourt: "Amtsgericht …",
  registerNumber: "HRB …",
  contentResponsible: "Name und Anschrift, falls abweichend",
  privacyContact: "z. B. datenschutz@… oder Name der/des Datenschutzbeauftragten",
  supervisoryAuthority: "Landesbeauftragte:r für Datenschutz des eigenen Bundeslands",
  hostingProvider: "z. B. Oracle Cloud Infrastructure, Rechenzentrum Frankfurt am Main",
  emailProvider: "z. B. Brevo, Postmark, Mailjet …",
  backupProvider: "z. B. Hetzner Online GmbH (Storage Box, Deutschland)",
};

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader title={title} description={description} />
      <div className="grid gap-4 p-5 sm:grid-cols-2">{children}</div>
    </Card>
  );
}

export function AdminLegalPage() {
  const queryClient = useQueryClient();
  const legal = useQuery(orpc.legal.get.queryOptions());
  const [form, setForm] = useState<LegalSettings | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (legal.data && !form) setForm(legal.data.settings);
  }, [legal.data, form]);

  if (!legal.data || !form) return <Skeleton className="h-96" />;

  const input = (
    field: keyof LegalSettings,
    options: { required?: boolean; wide?: boolean; type?: string } = {},
  ) => (
    <div className={options.wide ? "sm:col-span-2" : undefined}>
      <Field label={`${LEGAL_FIELD_LABELS[field]}${options.required ? " *" : ""}`}>
        <Input
          type={options.type}
          value={form[field]}
          placeholder={PLACEHOLDERS[field]}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        />
      </Field>
    </div>
  );
  const area = (field: keyof LegalSettings, hint: string) => (
    <div className="sm:col-span-2">
      <Field label={LEGAL_FIELD_LABELS[field]} hint={hint}>
        <Textarea
          rows={5}
          value={form[field]}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        />
      </Field>
    </div>
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    try {
      const page = await client.admin.legal.update(form);
      queryClient.setQueryData(orpc.legal.get.queryKey(), page);
      setForm(page.settings);
      toast.success(
        page.missing.length ? "Gespeichert – es fehlen noch Pflichtangaben" : "Gespeichert",
      );
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  const missing = legal.data.missing;
  return (
    <form onSubmit={submit} className="space-y-6">
      <AdminPageHeader
        title="Rechtliches"
        description={
          legal.data.updatedAt
            ? `Zuletzt gespeichert ${formatDateTime(legal.data.updatedAt)}`
            : "Noch nicht ausgefüllt"
        }
        actions={
          <Button type="submit" loading={busy}>
            Speichern
          </Button>
        }
      />

      <Card className="flex gap-3 border-zinc-300 bg-white p-4 text-sm text-zinc-700">
        <Scale className="mt-0.5 size-5 shrink-0 text-zinc-500" />
        <div className="space-y-1">
          <p>
            Aus diesen Angaben entstehen{" "}
            {[
              ["/impressum", "Impressum"],
              ["/datenschutz", "Datenschutzerklärung"],
              ["/nutzungsbedingungen", "Nutzungsbedingungen"],
            ].map(([href, label], index) => (
              <span key={href}>
                {index > 0 ? (index === 2 ? " und " : ", ") : null}
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 font-medium text-brand-700 hover:underline"
                >
                  {label} <ExternalLink className="size-3" />
                </a>
              </span>
            ))}
            . Die Seiten sind öffentlich und von jeder Seite der App verlinkt.
          </p>
          <p className="text-zinc-500">
            Die Texte sind eine sorgfältige Vorlage für diese Anwendung, aber keine Rechtsberatung.
            Bitte vor dem Öffnen für andere prüfen lassen.
          </p>
        </div>
      </Card>

      {missing.length > 0 ? (
        <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Es fehlen noch:{" "}
          {missing.map((field) => LEGAL_FIELD_LABELS[field as keyof LegalSettings]).join(", ")}. Auf
          den öffentlichen Seiten sind die Lücken gelb markiert.
        </Card>
      ) : null}

      <Section title="Anbieter" description="Wer den Dienst betreibt (Impressum, § 5 DDG).">
        {input("operatorName", { required: true, wide: true })}
        {input("legalForm")}
        {input("representative")}
        {input("street", { required: true, wide: true })}
        {input("postalCode", { required: true })}
        {input("city", { required: true })}
        {input("country")}
      </Section>

      <Section title="Kontakt und Register">
        {input("email", { required: true, type: "email" })}
        {input("phone")}
        {input("vatId")}
        <div />
        {input("registerCourt")}
        {input("registerNumber")}
        {input("contentResponsible", { wide: true })}
      </Section>

      <Section
        title="Datenschutz"
        description="Für die Datenschutzerklärung: Kontakt, Aufsicht und die eingesetzten Dienstleister (Auftragsverarbeiter)."
      >
        {input("privacyContact", { wide: true })}
        {input("supervisoryAuthority", { wide: true })}
        {input("hostingProvider", { required: true, wide: true })}
        {input("emailProvider", { wide: true })}
        {input("backupProvider", { wide: true })}
      </Section>

      <Card>
        <CardHeader
          title="Zusätzliche Texte"
          description="Werden am Ende der jeweiligen Seite angehängt. Absätze mit Leerzeile trennen, kein HTML."
        />
        <div className="grid gap-4 p-5">
          {area("extraImprint", "z. B. Hinweise zu Bildrechten")}
          {area("extraPrivacy", "z. B. weitere Dienstleister oder Besonderheiten")}
          {area("extraTerms", "z. B. Preise, sobald es Tarife gibt")}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" loading={busy}>
          Speichern
        </Button>
      </div>
    </form>
  );
}
